import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import MillionMileTicker from "@/components/MillionMileTicker";
import { Button } from "@/components/ui/button";
import PublicFooter from "@/components/PublicFooter";
import { Check, Crown, Sparkles, ShieldCheck, ArrowRight, Lock, Flame, Zap, RefreshCw, ChevronDown, Clock } from "lucide-react";
import NewPatientDealCard from "@/components/NewPatientDealCard";
import FreeShippingTruckBanner from "@/components/FreeShippingTruckBanner";
import LanguageToggle from "@/components/LanguageToggle";
import ServingStatesStrip from "@/components/ServingStatesStrip";
import { trackSignupClick } from "@/lib/track";

// Manufacturer/retail comparison price (per month) shown struck-through on
// every tier so patients see how far below the original list price the GLPFlo price is.
const MSRP = { semaglutide: 299, tirzepatide: 399 };
const TIER_MONTHS = { monthly: 1, solo_subscribe: 1, royal: 3 };

const FW_COLORS = ["#f59e0b", "#ec4899", "#8b5cf6", "#22d3ee", "#34d399", "#fb7185"];
const FW_BOLT_COLORS = ["#a855f7", "#22d3ee", "#facc15", "#f472b6", "#818cf8", "#c084fc"];

/**
 * "Sign Up Now" call-to-action that flashes and shoots a repeating spark burst.
 * `tone="light"` = white button (dark backgrounds), `tone="purple"` = purple button.
 * `effect="lightning"` swaps the spark dots for animated lightning bolts;
 * `pulsate` adds a pulsing scale on the button itself.
 */
function FireworksSignUp({ testid = "signup", label = "Sign Up Now", tone = "purple", effect = "fireworks", pulsate = false, className = "" }) {
  const btnTone =
    tone === "light"
      ? "bg-white text-purple-700 hover:bg-purple-50"
      : "bg-purple-600 hover:bg-purple-700 text-white";
  const bolts = effect === "lightning";
  return (
    <div className={`relative inline-block ${className}`} data-testid={`${testid}-fw`}>
      {Array.from({ length: 12 }).map((_, i) =>
        bolts ? (
          <span
            key={i}
            className="fw-spark fw-bolt"
            style={{ "--a": `${i * 30}deg`, animationDelay: `${(i % 6) * 0.1}s`, color: FW_BOLT_COLORS[i % FW_BOLT_COLORS.length] }}
          >
            <Zap className="w-3.5 h-3.5" fill="currentColor" />
          </span>
        ) : (
          <span
            key={i}
            className="fw-spark"
            style={{ "--a": `${i * 30}deg`, animationDelay: `${(i % 6) * 0.11}s`, background: FW_COLORS[i % FW_COLORS.length] }}
          />
        )
      )}
      <Link to="/register" onClick={() => trackSignupClick(`pricing-${testid}`)} className="relative z-[2] inline-block">
        <Button
          className={`fw-flash ${pulsate ? "animate-pulse" : ""} rounded-full px-8 py-5 text-base font-bold shadow-lg shadow-purple-500/40 ${btnTone}`}
          data-testid={testid}
        >
          {bolts ? <Zap className="w-4 h-4 mr-1.5" /> : null}
          {label}
          {bolts ? null : <ArrowRight className="w-4 h-4 ml-1.5" />}
        </Button>
      </Link>
    </div>
  );
}

/**
 * New Patient Deal promo card with a medication dropdown. Semaglutide and
 * Tirzepatide are priced independently (admin-editable via loyalty_rules:
 * new_patient_price / new_patient_price_tirz). The headline price and the
 * checkout amount follow the selected medication.
 */
function NewPatientDealBanner({ promoRules, npStats }) {
  const semaPrice = Number(promoRules.new_patient_price || 79.99);
  const tirzPrice = Number(promoRules.new_patient_price_tirz || 99.99);
  const [npDrug, setNpDrug] = useState("semaglutide");
  const price = npDrug === "tirzepatide" ? tirzPrice : semaPrice;

  // Rolling 24h urgency countdown (per browser). Resets after it expires so the
  // offer always feels time-boxed without a hard-coded calendar date.
  const [remaining, setRemaining] = useState(null);
  useEffect(() => {
    const KEY = "glpflo_np_deal_deadline";
    let deadline = Number(localStorage.getItem(KEY));
    const now = Date.now();
    if (!deadline || deadline < now) {
      deadline = now + 24 * 60 * 60 * 1000;
      localStorage.setItem(KEY, String(deadline));
    }
    const tick = () => setRemaining(Math.max(0, deadline - Date.now()));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, []);
  const fmtCountdown = (ms) => {
    const s = Math.floor(ms / 1000);
    const h = String(Math.floor(s / 3600)).padStart(2, "0");
    const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
    const sec = String(s % 60).padStart(2, "0");
    return `${h}:${m}:${sec}`;
  };
  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white p-6 sm:p-7 shadow-xl"
      data-testid="new-patient-deal-card-banner"
    >
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl" />
      <div className="relative">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-xs font-semibold">
          <Zap className="w-3.5 h-3.5" /> New to GLP-1?
        </div>
        <h3 className="text-2xl sm:text-3xl font-bold mt-3" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="new-patient-deal-price">
          New Patient Deal — First month ${price.toFixed(2)}
        </h3>
        {remaining != null && remaining > 0 && (
          <div className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-full bg-amber-400 text-purple-950 text-xs font-extrabold shadow animate-pulse" data-testid="new-patient-deal-countdown">
            <Clock className="w-3.5 h-3.5" /> Limited-time price — ends in <span className="tabular-nums tracking-wider">{fmtCountdown(remaining)}</span>
          </div>
        )}
        <p className="text-white/90 text-sm leading-relaxed mt-2">
          Available <strong>only on the 3-month Royal tier</strong>. Your first month is discounted to ${price.toFixed(2)}; months 2 and 3 bill at the regular Royal-tier price. Includes physician consult, cold-chain shipping, and unlimited messaging.
        </p>
        <div className="mt-3">
          <label htmlFor="np-drug-select" className="block text-[11px] uppercase tracking-wider font-semibold text-white/70 mb-1">
            Choose your medication
          </label>
          <div className="relative inline-block">
            <select
              id="np-drug-select"
              value={npDrug}
              onChange={(e) => setNpDrug(e.target.value)}
              className="appearance-none rounded-xl bg-white text-slate-900 font-semibold text-sm pl-4 pr-10 py-2.5 shadow-sm focus:outline-none focus:ring-2 focus:ring-white/70 cursor-pointer"
              data-testid="new-patient-drug-select"
            >
              <option value="semaglutide">{`Semaglutide — $${semaPrice.toFixed(2)}`}</option>
              <option value="tirzepatide">{`Tirzepatide — $${tirzPrice.toFixed(2)}`}</option>
            </select>
            <ChevronDown className="w-4 h-4 text-slate-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>
        <p className="text-white/70 text-[11px] mt-2">
          First-time GLPFlo patients only · Royal 3-month plan only · attestation required at checkout.
        </p>
        {npStats && npStats.unique_last_30d >= 5 && (
          <div
            className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/25 text-xs font-medium"
            data-testid="new-patient-social-proof"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            <span>
              <strong className="font-semibold">{npStats.unique_last_30d}</strong> patients started with this deal in the last 30 days
            </span>
          </div>
        )}
        <div className="mt-4">
          <NewPatientDealCard amount={price} presetDrug={npDrug} variant="inline-cta" />
        </div>
      </div>
    </div>
  );
}


/** Public /pricing page. Reads from `db.pricing_tiers` (admin-editable).
 *  Hero → 3-tier pricing per drug → no-hidden-fees comparison strip → CTA → footer. */
export default function PricingPage() {
  const [drugs, setDrugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promoRules, setPromoRules] = useState(null);
  const [npStats, setNpStats] = useState(null);
  const [ssStats, setSsStats] = useState(null);
  const [geo, setGeo] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get("/pricing");
        setDrugs(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Load pricing error:", e);
      } finally {
        setLoading(false);
      }
    })();
    API.get("/loyalty-rules/public")
      .then(({ data }) => setPromoRules(data))
      .catch(() => setPromoRules(null));
    API.get("/new-patient-deal/stats")
      .then(({ data }) => setNpStats(data))
      .catch(() => setNpStats(null));
    API.get("/switch-save/stats")
      .then(({ data }) => setSsStats(data))
      .catch(() => setSsStats(null));
    API.get("/geo/state")
      .then(({ data }) => setGeo(data))
      .catch(() => setGeo(null));
  }, []);

  // Inject Product + AggregateOffer JSON-LD once pricing data lands. Google
  // uses this to render price-range and "from $X/month" snippets directly
  // in the SERP for queries like "compounded semaglutide cost". One <Product>
  // per drug; AggregateOffer collapses all four tier prices into a
  // low/high range so we don't over-flag every dose variant as a separate
  // SKU (which triggers "over-broken product markup" warnings).
  useEffect(() => {
    if (!drugs.length) return undefined;
    const TAG_ID = "pricing-jsonld";
    // Flatten every price across dose groups + top-level fallback fields
    // so the low/high range reflects the actual purchasable universe.
    const collectPrices = (d) => {
      const bucket = [];
      const push = (v) => {
        const n = Number(v);
        if (n > 0) bucket.push(n);
      };
      push(d.monthly_price);
      push(d.solo_subscribe_price);
      push(d.monthly_subscribe_price);
      push(d.royal_tier_price);
      (d.dose_groups || []).forEach((g) => {
        push(g.monthly_price);
        push(g.solo_subscribe_price);
        push(g.monthly_subscribe_price);
        push(g.royal_tier_price);
      });
      return bucket;
    };
    const products = drugs
      .map((d) => {
        const prices = collectPrices(d);
        if (!prices.length) return null;
        const lo = Math.min(...prices);
        const hi = Math.max(...prices);
        return {
          "@type": "Product",
          "@id": `https://glpflo.org/pricing#${d.drug_slug}`,
          name: `${d.drug_name} — Compounded GLP-1 Telehealth`,
          description: d.drug_subtitle || `Physician-supervised compounded ${d.drug_name} delivered monthly. Provider consultation, prescription, medication, and expedited cold shipping — one flat monthly price.`,
          category: "Prescription weight-loss medication (compounded GLP-1)",
          brand: { "@type": "Brand", name: "GLPFlo" },
          url: `https://glpflo.org/pricing#${d.drug_slug}`,
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "USD",
            lowPrice: lo.toFixed(2),
            highPrice: hi.toFixed(2),
            offerCount: prices.length,
            availability: "https://schema.org/InStock",
            eligibleRegion: {
              "@type": "Country",
              name: "United States",
            },
            seller: {
              "@type": "MedicalOrganization",
              name: "GLPFlo",
              url: "https://glpflo.org",
            },
          },
        };
      })
      .filter(Boolean);
    if (!products.length) return undefined;
    const schema = { "@context": "https://schema.org", "@graph": products };
    let tag = document.getElementById(TAG_ID);
    if (!tag) {
      tag = document.createElement("script");
      tag.type = "application/ld+json";
      tag.id = TAG_ID;
      document.head.appendChild(tag);
    }
    tag.textContent = JSON.stringify(schema);
    return () => {
      const existing = document.getElementById(TAG_ID);
      if (existing) existing.remove();
    };
  }, [drugs]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="pricing-page">
      <MillionMileTicker />
      <ServingStatesStrip />
      {/* Top nav */}
      <header className="sticky top-0 z-30 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
            GLP<span className="text-purple-600">Flo</span>
          </Link>
          <nav className="flex items-center gap-2 sm:gap-3 md:gap-4 text-sm">
            <a href="/#services" className="hidden lg:inline text-slate-600 hover:text-purple-600 dark:text-slate-300 dark:hover:text-white" data-testid="pricing-nav-services">Services</a>
            <a href="/#how-it-works" className="hidden lg:inline text-slate-600 hover:text-purple-600 dark:text-slate-300 dark:hover:text-white" data-testid="pricing-nav-how">How It Works</a>
            <a href="/#medications" className="hidden lg:inline text-slate-600 hover:text-purple-600 dark:text-slate-300 dark:hover:text-white" data-testid="pricing-nav-medications">Medications</a>
            <Link to="/pricing" className="hidden sm:inline text-slate-600 hover:text-purple-600 dark:text-slate-300 dark:hover:text-white" data-testid="pricing-nav-pricing">Pricing</Link>
            <Link to="/faq" className="text-slate-600 hover:text-purple-600 dark:text-slate-300 dark:hover:text-white" data-testid="pricing-nav-faq">FAQ</Link>
            <Link to="/login" className="text-slate-600 hover:text-purple-600 dark:text-slate-300 dark:hover:text-white" data-testid="pricing-nav-login">Sign in</Link>
            <Link to="/register" onClick={() => trackSignupClick("pricing-nav-cta")}><Button className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-4 sm:px-5" data-testid="pricing-nav-cta">Get started</Button></Link>
          </nav>
        </div>
      </header>

      {/* Free Shipping truck banner — mirrors the one on the landing page so
          patients see shipping is included even when they land directly on
          /pricing from a search result. */}
      <FreeShippingTruckBanner />

      {/* Promotional deals — Switch & Save + New Patient Deal (moved ABOVE
          the hero so returning customers see actionable offers first). */}
      {promoRules && (promoRules.switch_save_active || promoRules.new_patient_deal_active) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 pb-10 sm:pb-14" data-testid="pricing-promos">
          <div className="grid md:grid-cols-2 gap-4">
            {promoRules.switch_save_active && (
              <div
                className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-fuchsia-600 text-white p-6 sm:p-7 shadow-xl"
                data-testid="switch-save-card"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl" />
                <div className="relative">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-xs font-semibold">
                    <RefreshCw className="w-3.5 h-3.5" /> Already on a GLP-1?
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold mt-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Switch &amp; Save — ${Number(promoRules.switch_save_amount || 20)} off
                  </h3>
                  <p className="text-white/90 text-sm leading-relaxed mt-2">
                    Continuing your weekly dose? We&apos;ll pick you up at the same tier. ${Number(promoRules.switch_save_amount || 20)} off your first order. <strong>Non-stackable</strong> with the New Patient Deal or the refer-a-friend coupon.
                  </p>
                  <p className="text-white/70 text-[11px] mt-2">
                    Eligibility: last dose within 2 weeks · first paid order only · auto-applied at checkout.
                  </p>
                  {ssStats && ssStats.switched_last_7d >= 5 && (
                    <div
                      className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/25 text-xs font-medium"
                      data-testid="switch-save-social-proof"
                    >
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                      </span>
                      <span>
                        <strong className="font-semibold">{ssStats.switched_last_7d}</strong> patients switched from a competitor this week
                      </span>
                    </div>
                  )}
                  <Link to="/register?promo=switch-save" className="inline-block mt-4">
                    <Button className="bg-white hover:bg-slate-100 text-purple-700 font-semibold rounded-full px-5" data-testid="switch-save-cta">
                      Claim my ${Number(promoRules.switch_save_amount || 20)} off <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            )}
            {promoRules.new_patient_deal_active && (
              <NewPatientDealBanner promoRules={promoRules} npStats={npStats} />
            )}
          </div>
        </section>
      )}

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-purple-700 via-indigo-700 to-slate-900 text-white">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-purple-500 blur-3xl"></div>
          <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-pink-500 blur-3xl"></div>
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-xs font-medium mb-3" data-testid="pricing-hero-badge">
            <Sparkles className="w-3.5 h-3.5" />
            Transparent pricing — what you see is what you pay
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="pricing-hero-title">
            One price. Everything included.
          </h1>
          <p className="text-sm sm:text-base text-white/80 max-w-2xl mx-auto leading-relaxed mb-5">
            Provider consultation is <strong className="text-white">included</strong> — no membership fee, no shipping cost, no cancellation penalty. One monthly price covers your provider visit, prescription, compounded medication, and expedited cold shipping.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap text-xs text-white/70 mb-6">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-300" /> U.S.-licensed providers</span>
            <span className="text-white/30">·</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-300" /> 503A compounding</span>
            <span className="text-white/30">·</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-300" /> Cancel anytime</span>
          </div>
          <div className="mt-8 sm:mt-12 pb-2">
            <FireworksSignUp testid="pricing-hero-signup" tone="light" />
          </div>
        </div>
      </section>

      {/* Pricing tiers per drug */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {loading ? (
          <div className="text-center text-slate-400 py-20">Loading pricing…</div>
        ) : drugs.length === 0 ? (
          <div className="max-w-3xl mx-auto text-center py-16" data-testid="pricing-static-fallback">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Simple, flat-rate GLP-1 pricing</h2>
            <p className="text-slate-300 text-lg mb-6">
              Compounded Semaglutide starts at <strong className="text-white">$149/month</strong> and Compounded Tirzepatide starts at <strong className="text-white">$249/month</strong>, with volume discounts on 2- and 3-month bundles. First-time patients on the <strong className="text-white">Royal 3-month plan</strong> get their first month discounted to <strong className="text-white">$79.99 (Semaglutide)</strong> or <strong className="text-white">$99.99 (Tirzepatide)</strong>; months 2 and 3 bill at the regular Royal-tier price.
            </p>
            <p className="text-slate-400 text-sm">
              Every plan includes a licensed U.S. physician consultation, prescription, <strong className="text-white">free cold-chain shipping</strong>, and unlimited messaging with your care team — no hidden fees, no insurance required, HIPAA-compliant.
            </p>
          </div>
        ) : (
          <div className="space-y-16">
            {drugs.map((d) => (
              <DrugTiers key={d.id} drug={d} />
            ))}
          </div>
        )}
        {!loading && drugs.length > 0 && (
          <div className="mt-12 flex justify-center" data-testid="pricing-tiers-signup-wrap">
            <FireworksSignUp testid="pricing-tiers-signup" tone="purple" />
          </div>
        )}
      </section>

      {/* No-hidden-fees comparison strip */}
      <section className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-900/50" data-testid="pricing-comparison-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-sm sm:text-base font-semibold text-amber-900 dark:text-amber-200 mb-4">
            What other telehealth brands quietly charge — and what we don&apos;t.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-center">
            {[
              { label: "Consult fee", other: "$50–$100", us: "Included" },
              { label: "Membership fee", other: "$30–$99/mo", us: "$0" },
              { label: "Shipping", other: "$15+ extra", us: "$0" },
              { label: "Cancellation fee", other: "$50+", us: "$0" },
            ].map((row) => (
              <div key={row.label} className="bg-white dark:bg-slate-900 rounded-xl p-3 sm:p-4 border border-amber-200/60 dark:border-amber-900/40">
                <p className="text-[11px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{row.label}</p>
                <p className="text-xs sm:text-sm text-slate-400 line-through mt-1">Others: {row.other}</p>
                <p className="text-base sm:text-lg font-bold text-emerald-600 dark:text-emerald-400 mt-1">GLPFlo: {row.us}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Honest Fees / No Hidden Costs transparency panel */}
      <section className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800" data-testid="pricing-honest-fees">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/60 text-xs font-semibold text-emerald-700 dark:text-emerald-300 mb-3">
              <ShieldCheck className="w-3.5 h-3.5" /> Nothing hidden. Ever.
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Every charge, written down. In plain English.
            </h2>
            <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 max-w-2xl mx-auto mt-3">
              We don&apos;t bury fees in fine print. Here is every dollar that could ever come out of (or go back into) your card, and exactly when.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border-2 border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/60 dark:bg-emerald-950/30 p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-2">What you pay</p>
              <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-200">
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span>The monthly price listed on this page — that&apos;s <strong>it</strong>. Covers your provider visit, prescription, compounded medication, and cold-chain shipping.</span></li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span><strong>$0</strong> consultation fee at signup — the consultation is included in the monthly price.</span></li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span><strong>$0</strong> membership, <strong>$0</strong> shipping, <strong>$0</strong> cancellation fee.</span></li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span><strong>Full Companion App included</strong> — food logging, macro rings, Fit-Quiz projection, and an AI wellness assistant (not medical advice; clinician review always separate). (Rewards, shot reminders, and body tracking are free for everyone, subscription or not.)</span></li>
              </ul>
            </div>
            <div className="rounded-2xl border-2 border-amber-200 dark:border-amber-900/50 bg-amber-50/60 dark:bg-amber-950/30 p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300 mb-2">What you get back if you cancel</p>
              <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-200">
                <li className="flex gap-2"><span className="mt-0.5 w-4 h-4 shrink-0 rounded-full bg-amber-200 text-amber-900 text-[10px] font-bold flex items-center justify-center">1</span><span><strong>Before your order ships</strong> &amp; we can stop it in time: full refund <strong>minus a flat $25 consultation fee</strong> (the provider review already happened).</span></li>
                <li className="flex gap-2"><span className="mt-0.5 w-4 h-4 shrink-0 rounded-full bg-amber-200 text-amber-900 text-[10px] font-bold flex items-center justify-center">2</span><span><strong>After it ships:</strong> all sales are final — the medication was custom-compounded for you.</span></li>
                <li className="flex gap-2"><span className="mt-0.5 w-4 h-4 shrink-0 rounded-full bg-amber-200 text-amber-900 text-[10px] font-bold flex items-center justify-center">3</span><span>If your provider recommends a different strength than you ordered and you decline: refund minus the same $25 consult fee.</span></li>
                <li className="flex gap-2"><span className="mt-0.5 w-4 h-4 shrink-0 rounded-full bg-emerald-200 text-emerald-900 text-[10px] font-bold flex items-center justify-center">✓</span><span><strong>Provider denies your order entirely (medical denial):</strong> <strong>full refund — no $25 retained.</strong></span></li>
                <li className="flex gap-2"><span className="mt-0.5 w-4 h-4 shrink-0 rounded-full bg-amber-200 text-amber-900 text-[10px] font-bold flex items-center justify-center">!</span><span>If you request a dose <strong>above the safe starter</strong> while not meeting our criteria to start higher, you&apos;ll check an acknowledgement — if denied, the $25 consult fee is retained.</span></li>
              </ul>
            </div>
          </div>
          {/* Lightning-effect Sign Up Now, above the shipping-address callout */}
          <div className="mt-8 flex justify-center" data-testid="pricing-lightning-signup-wrap">
            <FireworksSignUp testid="pricing-lightning-signup" tone="purple" effect="lightning" pulsate />
          </div>
          {/* Address accuracy callout */}
          <div className="mt-6 rounded-2xl border-2 border-rose-200 dark:border-rose-900/50 bg-rose-50/70 dark:bg-rose-950/30 p-5" data-testid="pricing-address-accuracy-callout">
            <div className="flex gap-3 items-start">
              <div className="w-10 h-10 rounded-xl bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center shrink-0">
                <Lock className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 dark:text-white">Please double-check your shipping address at checkout.</p>
                <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                  The #1 reason a package goes missing is a typo in the address — a missing apartment number, an old address auto-filled by your browser, or a wrong ZIP. Once we ship, we can&apos;t redirect it, and an incorrect address is not a refundable reason. We&apos;d rather take 10 extra seconds at checkout than disappoint you later.
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                  Read the full policy in our <Link to="/terms-of-service" className="text-purple-700 dark:text-purple-300 underline font-medium" data-testid="pricing-tos-link">Terms of Service</Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Trust + CTA */}
      <section className="bg-gradient-to-br from-slate-900 to-purple-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Ready to start?
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-8 leading-relaxed">
            Create an account, complete your medical questionnaire, and a U.S.-licensed clinician will review and prescribe — usually within 24 hours.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/register" onClick={() => trackSignupClick("pricing-bottom-cta")}>
              <Button className="bg-white text-purple-700 hover:bg-purple-50 rounded-xl px-6 py-5 text-base font-semibold" data-testid="pricing-cta-register">
                Get started <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
            <Link to="/faq">
              <Button variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10 rounded-xl px-6 py-5 text-base" data-testid="pricing-cta-faq">
                Read FAQ
              </Button>
            </Link>
          </div>
          <p className="text-xs text-white/50 mt-6 inline-flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5" /> Encrypted, HIPAA-compliant. Your data is never sold.
          </p>
          <p className="text-[11px] text-white/40 mt-3 max-w-xl mx-auto" data-testid="pricing-statement-descriptor">
            Charges from GLPFlo appear on your card statement as <span className="font-semibold text-white/60">MCPECK GROUP LLC</span>, our payment processor&apos;s billing descriptor. Both names refer to the same order.
          </p>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

/** Three-tier pricing card cluster for a single drug. Royal = featured/highlighted.
 *  When the drug has `dose_groups`, a dose selector appears above the cards
 *  and the prices + savings-calculator swap to the selected dose. Doses that
 *  share a vial size share a price (enforced in the seed/admin). */
function DrugTiers({ drug }) {
  const doseGroups = Array.isArray(drug.dose_groups) ? drug.dose_groups : [];
  const hasDoseGroups = false; // All doses are the SAME price — dose picker removed.
  // Default to the "mid" group when present, otherwise the first one. Mid is
  // the most common patient tier and the flagship price point.
  const defaultDoseKey = (doseGroups.find((g) => g.key === "mid") || doseGroups[0])?.key;
  const [doseKey, setDoseKey] = useState(defaultDoseKey);
  const selectedDose = hasDoseGroups
    ? (doseGroups.find((g) => g.key === doseKey) || doseGroups[0])
    : null;

  // Resolved per-tier prices: fall back to the drug-level prices if no dose
  // groups are defined (legacy single-price shape).
  const monthlyPrice = selectedDose?.monthly_price ?? drug.monthly_price;
  const soloPrice = selectedDose?.solo_subscribe_price ?? drug.solo_subscribe_price ?? Math.max(0, (Number(monthlyPrice) || 0) - 4);
  const royalPrice = selectedDose?.royal_tier_price ?? drug.royal_tier_price;

  const tiers = [
    {
      key: "monthly",
      label: "Monthly",
      sublabel: "One-time, no commitment",
      price: monthlyPrice,
      cadence: "/month",
      features: drug.monthly_features || [],
      cta: "Start monthly",
      ctaTo: `/register?plan=${drug.drug_slug}-monthly${doseKey ? `-${doseKey}` : ""}`,
      featured: false,
    },
    {
      key: "solo_subscribe",
      label: "1-Month Subscribe",
      sublabel: "1 vial auto-renew — $4 off",
      price: soloPrice,
      cadence: "/month",
      features: drug.solo_subscribe_features || [],
      cta: "Subscribe monthly",
      ctaTo: `/register?plan=${drug.drug_slug}-solo${doseKey ? `-${doseKey}` : ""}`,
      featured: false,
    },
    {
      key: "royal",
      label: "3-Month Subscribe",
      sublabel: "Our deepest discount",
      price: royalPrice,
      cadence: "/month",
      features: drug.royal_tier_features || [],
      cta: "Subscribe & save",
      ctaTo: `/register?plan=${drug.drug_slug}-royal${doseKey ? `-${doseKey}` : ""}`,
      featured: true,
      popular: true,
    },
  ];

  return (
    <div data-testid={`pricing-drug-${drug.drug_slug}`}>
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 text-sm font-medium text-purple-600 dark:text-purple-400 mb-2">
          <span className="text-2xl leading-none">{drug.drug_emoji || "💊"}</span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {drug.drug_name}
        </h2>
        {drug.drug_subtitle && (
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">{drug.drug_subtitle}</p>
        )}
      </div>

      {hasDoseGroups && (
        <DoseSelector drug={drug} groups={doseGroups} selected={doseKey} onSelect={setDoseKey} />
      )}

      {/* Same price across every dose — a GLPFlo promise */}
      <div className="mb-6 flex justify-center" data-testid={`same-price-banner-${drug.drug_slug}`}>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/60 text-xs sm:text-sm font-semibold text-emerald-700 dark:text-emerald-300">
          <Check className="w-4 h-4" /> Same price at <strong>every dose</strong> — we never raise your price as your dose goes up.
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {tiers.map((t) => (
          <TierCard key={t.key} drug={drug} tier={t} />
        ))}
      </div>

      {/* Coupon disclaimer */}
      <p className="mt-8 text-center text-[11px] text-slate-400 dark:text-slate-500 max-w-2xl mx-auto" data-testid={`coupon-disclaimer-${drug.drug_slug}`}>
        Coupons reduce the price for that <strong>one-time purchase only</strong> — the discounted rate does not carry over to future refills or recurring charges.
      </p>
    </div>
  );
}

/** Dose-group selector. Doses that share a vial share a price, so patients
 *  pick their dose once and all three tier cards update in lock-step. */
function DoseSelector({ drug, groups, selected, onSelect }) {
  return (
    <div className="mb-6" data-testid={`dose-selector-${drug.drug_slug}`}>
      <p className="text-center text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-3">
        Pick your weekly dose
      </p>
      <div className="flex items-center justify-center flex-wrap gap-2">
        {groups.map((g) => {
          const active = g.key === selected;
          return (
            <button
              key={g.key}
              type="button"
              onClick={() => onSelect(g.key)}
              className={`rounded-xl px-4 py-3 border-2 text-left transition-all min-w-[150px] ${
                active
                  ? "border-purple-600 bg-purple-50 dark:bg-purple-900/40 shadow-md"
                  : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-purple-300 dark:hover:border-purple-700"
              }`}
              data-testid={`dose-option-${drug.drug_slug}-${g.key}`}
              aria-pressed={active}
            >
              <p className={`text-[10px] uppercase tracking-wider font-bold ${active ? "text-purple-700 dark:text-purple-300" : "text-slate-500 dark:text-slate-400"}`}>
                {g.label}
              </p>
              <p className={`text-sm font-semibold mt-0.5 ${active ? "text-slate-900 dark:text-white" : "text-slate-700 dark:text-slate-200"}`}>
                {g.dose_range}
              </p>
              {g.vial_size && (
                <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">{g.vial_size}</p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TierCard({ drug, tier }) {
  const featured = tier.featured;
  const popular = tier.popular;
  const msrp = MSRP[drug.drug_slug];
  const months = TIER_MONTHS[tier.key] || 1;
  const tierTotal = (Number(tier.price) || 0) * months;
  const msrpTotal = msrp ? msrp * months : 0;
  const save = msrp ? Math.max(0, Math.round(msrpTotal - tierTotal)) : 0;
  const savePeriod = months === 3 ? "over 3 months" : "every month";
  return (
    // Outer wrapper is NOT overflow-hidden — so the "Best value" pill and
    // "Most popular" chip can peek above the card's top edge without getting
    // clipped by the shimmer container.
    <div
      className={`relative ${featured ? "lg:scale-105" : ""}`}
      data-testid={`tier-${drug.drug_slug}-${tier.key}`}
    >
      {featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900 text-amber-300 text-xs font-bold uppercase tracking-wider shadow z-[20]">
          <Crown className="w-3.5 h-3.5" /> Best value
        </div>
      )}
      {popular && !featured && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-purple-600 text-white text-[10px] font-semibold uppercase tracking-wider shadow-md shadow-purple-500/30 z-[20]"
          data-testid={`tier-${drug.drug_slug}-${tier.key}-popular-chip`}
        >
          <Flame className="w-3 h-3" /> Most popular
        </div>
      )}
      <div
        className={`relative rounded-2xl p-7 transition-all duration-300 ${
          featured
            ? "bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 text-orange-300 shadow-2xl shadow-purple-500/40 border-2 border-purple-300 shimmer-pill"
            : popular
              ? "bg-white dark:bg-slate-900 border-2 border-purple-300 dark:border-purple-700 hover:border-purple-400 hover:shadow-xl ring-1 ring-purple-200/50"
              : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-xl"
        }`}
      >
      {featured && (
        <>
          {/* Outer warm glow — mirrors the no-insurance pill aesthetic but
              committed to the orange/amber palette. */}
          <span aria-hidden className="absolute -inset-0.5 rounded-2xl bg-gradient-to-br from-purple-400/50 via-fuchsia-400/40 to-indigo-500/50 blur-xl -z-10" />

          {/* Floating sparkles — staggered so they twinkle independently. */}
          <span aria-hidden className="absolute top-4 left-6 text-[12px] shimmer-sparkle text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.95)] z-[2]" style={{ animationDelay: "0s" }}>✦</span>
          <span aria-hidden className="absolute top-10 right-8 text-[10px] shimmer-sparkle text-amber-50 drop-shadow-[0_0_6px_rgba(255,255,255,0.9)] z-[2]" style={{ animationDelay: "0.5s" }}>✧</span>
          <span aria-hidden className="absolute bottom-24 left-10 text-[9px] shimmer-sparkle text-white drop-shadow-[0_0_5px_rgba(255,255,255,0.9)] z-[2]" style={{ animationDelay: "1.1s" }}>✦</span>
          <span aria-hidden className="absolute bottom-32 right-6 text-[11px] shimmer-sparkle text-amber-50 drop-shadow-[0_0_7px_rgba(255,255,255,0.95)] z-[2]" style={{ animationDelay: "1.7s" }}>✧</span>
          <span aria-hidden className="absolute top-1/2 right-12 text-[8px] shimmer-sparkle text-white drop-shadow-[0_0_5px_rgba(255,255,255,0.9)] z-[2]" style={{ animationDelay: "2.2s" }}>✦</span>
        </>
      )}

      <p className={`relative z-[2] text-xs font-bold uppercase tracking-wider ${featured ? "text-orange-400" : "text-slate-400 dark:text-slate-500"}`}>
        {tier.label}
      </p>
      <p className={`relative z-[2] text-sm mt-1 font-medium ${featured ? "text-orange-300" : "text-slate-400 dark:text-slate-600"}`}>
        {tier.sublabel}
      </p>
      {msrp > 0 && (
        <p className={`relative z-[2] mt-3 text-sm line-through opacity-60 ${featured ? "text-orange-200" : "text-slate-400 dark:text-slate-500"}`} data-testid={`tier-msrp-${drug.drug_slug}-${tier.key}`}>
          Original price ${msrpTotal.toLocaleString()}{months === 1 ? "/mo" : ""}
        </p>
      )}
      <div className="relative z-[2] mt-2 flex items-baseline gap-1">
        <span
          className={`text-5xl font-extrabold ${featured ? "text-orange-400" : "text-slate-400 dark:text-slate-500"}`}
          style={{
            fontFamily: "'Outfit', sans-serif",
            ...(featured
              ? {
                  WebkitTextStrokeWidth: "1.5px",
                  WebkitTextStrokeColor: "rgba(0,0,0,0.9)",
                  paintOrder: "stroke",
                  textShadow: "0 1px 2px rgba(0,0,0,0.45)",
                }
              : {}),
          }}
        >
          ${tier.price}
        </span>
        <span className={`text-base font-medium ${featured ? "text-orange-300" : "text-slate-400 dark:text-slate-600"}`}>{tier.cadence}</span>
      </div>
      {msrp > 0 && save > 0 && (
        <div className="relative z-[2] mt-2" data-testid={`tier-savings-${drug.drug_slug}-${tier.key}`}>
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-extrabold animate-pulse shadow-lg ${
              featured
                ? "bg-orange-400 text-purple-900 shadow-orange-400/40"
                : "bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-emerald-500/40"
            }`}
            style={{ animationDuration: "1.4s" }}
          >
            <Sparkles className="w-3.5 h-3.5" /> You save ${save.toLocaleString()} {savePeriod}
          </span>
        </div>
      )}
      <ul className="relative z-[2] mt-6 space-y-3 min-h-[180px]">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm">
            <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${featured ? "text-orange-300" : "text-slate-300 dark:text-slate-600"}`} />
            <span className={featured ? "text-orange-200 font-medium" : "text-slate-400 dark:text-slate-500"}>{f}</span>
          </li>
        ))}
      </ul>
      <div className="relative z-[2] mt-7">
        {featured && Array.from({ length: 12 }).map((_, i) => (
          <span
            key={i}
            className="fw-spark"
            style={{
              "--a": `${i * 30}deg`,
              animationDelay: `${(i % 6) * 0.11}s`,
              background: FW_COLORS[i % FW_COLORS.length],
            }}
          />
        ))}
        <Link to={tier.ctaTo} className="relative z-[2] block">
          <Button
            className={`w-full rounded-xl py-5 text-base font-semibold ${
              featured
                ? "fw-flash bg-orange-400 text-purple-900 hover:bg-orange-300 shadow-lg shadow-purple-900/20"
                : "bg-purple-600 hover:bg-purple-700 text-white"
            }`}
            data-testid={`tier-cta-${drug.drug_slug}-${tier.key}`}
          >
            {tier.cta}
          </Button>
        </Link>
      </div>
      </div>
    </div>
  );
}
